import { useState } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { modelCourseSwap, GRADE_ORDER } from '@gpa-scales/shared-logic/engine/scenarioModeling';
import type { Course, AppSettings, ScenarioResult } from '@gpa-scales/shared-types';
import { ScenarioResultTable } from './ScenarioResultTable';
import { Select } from 'calc-controls';

export function CourseSwapTab({ courses, settings }: { courses: Course[]; settings: AppSettings }) {
  const [selectedId, setSelectedId] = useState('');
  const [newGrade, setNewGrade] = useState('A');
  const [result, setResult] = useState<ScenarioResult | null>(null);
  const [scenarioName, setScenarioName] = useState('');
  const saveScenario = useAppStore((s) => s.saveScenario);

  const swap = () => {
    if (!selectedId) return;
    const existing = courses.find((c) => c.id === selectedId);
    if (!existing) return;

    const replacement = { ...existing, grade: newGrade };
    delete (replacement as Record<string, unknown>).id;

    const res = modelCourseSwap(courses, selectedId, replacement, settings);
    setResult(res);
  };

  const handleSave = () => {
    if (!result) return;
    const name = scenarioName.trim() || `Swap ${new Date().toLocaleTimeString()}`;
    saveScenario(name, result);
    setScenarioName('');
  };

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor="swap-course" className="mb-1 block text-xs text-[var(--color-text-secondary)]">Select course to swap</label>
        <Select
          inline
          label="Select course to swap"
          value={selectedId}
          onChange={setSelectedId}
          options={[
            { value: '', label: 'Choose a course...' },
            ...courses.map((c) => ({ value: c.id, label: `${c.name} (${c.grade})` })),
          ]}
        />
      </div>
      <div>
        <label htmlFor="swap-grade" className="mb-1 block text-xs text-[var(--color-text-secondary)]">New Grade</label>
        <Select
          inline
          label="New Grade"
          value={newGrade}
          onChange={setNewGrade}
          options={GRADE_ORDER.map((g) => ({ value: g, label: g }))}
        />
      </div>
      <button onClick={swap} data-testid="scenario-compare" className="rounded-md bg-success px-3 py-1.5 text-xs font-medium text-white">
        Compare
      </button>
      {result && (
        <div className="rounded border border-border bg-[var(--color-bg-secondary)] p-3 text-xs">
          <ScenarioResultTable result={result} />
          {/* R37 — Save button */}
          <div className="mt-3 flex items-center gap-2">
            <input
              type="text"
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              placeholder="Scenario name (optional)"
              aria-label="Scenario name"
              className="flex-1 rounded border border-border px-2 py-1 text-xs"
            />
            <button
              onClick={handleSave}
              className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-white hover:bg-primary-hover"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
